/**
 * Sidebar navigation config + pure filter helper (Feb 2026, Iter236 IA).
 *
 * Iter236 IA refactor:
 *   - Every entry declares `groupId` (one of `NAV_GROUPS`) + `order`
 *     (used both to place items in the group AND to place groups in
 *     the sidebar).
 *   - Every leaf entry declares `isPinnable` (true for every main +
 *     sub page). P2 will consume this to render a `<PinnedSection>`
 *     backed by `/api/nav-pins`.
 *   - Group headers are injected automatically by `buildSidebarWithGroups()`
 *     so the source-of-truth registry stays a flat array.
 *   - Dashboard removed from nav (kept as a compat route in App.jsx →
 *     `<Navigate to="/home" />`) so we no longer have two "home-like"
 *     pages competing for the same mental model.
 *
 * Each nav entry is tagged with a `module` (feature flag gate):
 *   - `'uk_workflows'` → UK / HMRC / RGR-only surfaces
 *   - `'eu_lane'`      → EU lane surfaces
 *   - (undefined)      → generic / cross-module surfaces
 *
 * Stable test ids: every entry carries `testId: 'nav-<slug>'`.
 *
 * Pure functions only — no React imports so unit tests can run
 * without DOM setup.
 */

export const NAV_MODULES = {
  EU_LANE: 'eu_lane',
  USA_LANE: 'usa_lane',
  UK_WORKFLOWS: 'uk_workflows',
};

// Iter236 — 7 canonical groups. The `order` field on each group + on
// each item deterministically drives sidebar layout. `visibility`
// controls whether external roles (merchant/broker) can see the
// group header at all (defence-in-depth on top of role gates).
export const NAV_GROUPS = {
  HOME: {
    id: 'home', label: 'Home', order: 10, iconKey: 'Home',
    visibility: 'external_ok',
  },
  WORKFLOWS: {
    id: 'workflows', label: 'Workflows', order: 20, iconKey: 'GitBranch',
    visibility: 'external_ok',
  },
  OPERATIONS: {
    id: 'operations', label: 'Operations', order: 30, iconKey: 'Inbox',
    visibility: 'external_ok',
  },
  INSIGHTS: {
    id: 'insights', label: 'Insights & Reports', order: 40,
    iconKey: 'TrendingUp', visibility: 'external_ok',
  },
  DATA: {
    id: 'data', label: 'Data & Integrations', order: 50,
    iconKey: 'Database', visibility: 'external_ok',
  },
  ADMIN: {
    id: 'admin', label: 'Admin', order: 60, iconKey: 'Shield',
    visibility: 'external_ok',
  },
  GROWTH: {
    id: 'growth', label: 'Growth', order: 70, iconKey: 'Users',
    visibility: 'internal_only',
  },
  // Iter263 — Partner Admin (3PL / Agency tier) group. Only visible
  // to partner_admin role; sysadmin already has the full growth tree.
  PARTNER: {
    id: 'partner', label: 'Partner', order: 15, iconKey: 'Building2',
    visibility: 'external_ok',
  },
};

// Shortcut role bundles — keep readable.
// Iter263 Phase T2 — `_ALL` now includes partner_admin + partner_viewer
// so shared surfaces (Home / Overview, Recent Activity, etc.) surface
// for the 3PL tier. Write-side isolation is enforced at the API.
const _ALL = ['system_admin', 'client_admin', 'merchant_admin',
              'merchant_operator', 'merchant_viewer', 'viewer',
              'meridian_operator', 'meridian_approver',
              'broker_collaborator', '3pl_operator',
              'partner_admin', 'partner_viewer'];
const _INTERNAL = ['system_admin', 'meridian_operator', 'meridian_approver'];
const _INTERNAL_SYSADMIN = ['system_admin'];
const _INTERNAL_OPS = ['system_admin', 'meridian_operator'];
const _MERCHANT_ADMIN = ['system_admin', 'client_admin', 'merchant_admin'];

export const SIDEBAR_NAV = [
  // ---------------------------- HOME ------------------------------------
  { name: 'Overview', href: '/home', testId: 'nav-home', iconKey: 'Home',
    groupId: 'home', order: 10, isPinnable: true,
    roles: _ALL, highlight: true },

  // -------------------------- WORKFLOWS ---------------------------------
  { name: 'All Workflows', href: '/workflows', testId: 'nav-workflows',
    iconKey: 'GitBranch', groupId: 'workflows', order: 10,
    isPinnable: true, module: NAV_MODULES.UK_WORKFLOWS,
    roles: ['system_admin', 'meridian_operator', 'meridian_approver', 'client_admin',
            'partner_admin', 'partner_viewer'],
    highlight: true },
  // Phase 31 — "Import Repayment v2" (/import-review) retired.
  { name: 'Export Declarations', href: '/export-declarations',
    testId: 'nav-export-declarations', iconKey: 'FileOutput',
    groupId: 'workflows', order: 30, isPinnable: true,
    module: NAV_MODULES.UK_WORKFLOWS,
    roles: ['meridian_operator', 'meridian_approver', 'system_admin',
            'client_admin', 'merchant_admin', 'merchant_operator',
            'broker_collaborator',
            'partner_admin', 'partner_viewer'] },
  { name: 'EU Returns', href: '/eu-returns', testId: 'nav-eu-returns',
    iconKey: 'Globe2', groupId: 'workflows', order: 40, isPinnable: true,
    module: NAV_MODULES.EU_LANE, lane: 'EU',
    roles: ['system_admin', 'client_admin', 'meridian_operator', 'meridian_approver',
            'partner_admin', 'partner_viewer'] },
  // Iter255 — the following three items are sub-items of "EU Returns".
  // They render nested under the EU Returns nav entry (parent: 'eu.returns'
  // in the route registry) and appear immediately after it in the sidebar.
  { name: 'EU H7 Compilation', href: '/eu-h7-compilation',
    testId: 'nav-eu-h7-compilation', iconKey: 'FileCheck',
    groupId: 'workflows', order: 41, isPinnable: true,
    parentHref: '/eu-returns',
    module: NAV_MODULES.EU_LANE, lane: 'EU',
    roles: ['system_admin', 'meridian_operator', 'meridian_approver',
            'partner_admin', 'partner_viewer'] },
  { name: 'EU H7 Reason Mapper', href: '/eu-h7-reason-mapper',
    testId: 'nav-eu-h7-reason-mapper', iconKey: 'MessageSquare',
    groupId: 'workflows', order: 42, isPinnable: true,
    parentHref: '/eu-returns',
    module: NAV_MODULES.EU_LANE, lane: 'EU',
    roles: ['system_admin', 'client_admin', 'merchant_admin', 'meridian_operator', 'meridian_approver',
            'partner_admin', 'partner_viewer'] },
  // Iter S18 Phase 8 — USA lane nav entry. Mirrors the EU Returns
  // entry above exactly (same shape, same rendering, same route-prefix
  // convention `/usa-…`). Gated by `NAV_MODULES.USA_LANE` — see
  // ``isModuleEnabled`` for the workspace-flag + partner-entitlement
  // fallback that surfaces this entry only when
  // ``lanes_enabled.usa === true``.
  { name: 'USA Returns', href: '/usa-returns', testId: 'nav-usa-returns',
    iconKey: 'Globe2', groupId: 'workflows', order: 44, isPinnable: true,
    module: NAV_MODULES.USA_LANE, lane: 'USA',
    roles: ['system_admin', 'client_admin', 'meridian_operator', 'meridian_approver',
            'partner_admin', 'partner_viewer'] },
  { name: 'Shipments', href: '/shipments', testId: 'nav-shipments',
    iconKey: 'Ship', groupId: 'workflows', order: 85, isPinnable: true,
    module: NAV_MODULES.UK_WORKFLOWS,
    roles: ['client_admin', 'merchant_admin', 'merchant_operator',
            'meridian_operator', 'meridian_approver', 'system_admin',
            'broker_collaborator',
            'partner_admin', 'partner_viewer'] },
  { name: 'Intake & Tracking', href: '/intake', testId: 'nav-intake',
    iconKey: 'Truck', groupId: 'workflows', order: 90, isPinnable: true,
    module: NAV_MODULES.UK_WORKFLOWS,
    roles: ['3pl_operator', 'meridian_operator', 'system_admin',
            'partner_admin', 'partner_viewer'] },
  { name: 'Filing Hub', href: '/filing-hub', testId: 'nav-filing-hub',
    iconKey: 'ShieldCheck', groupId: 'workflows', order: 95, isPinnable: true,
    roles: ['system_admin', 'meridian_operator', 'meridian_approver', 'client_admin', 'merchant_admin',
            'merchant_operator', 'merchant_viewer', 'viewer', 'partner_admin', 'partner_viewer'] },
  { name: 'Case Approvals', href: '/approvals',
    testId: 'nav-case-approvals', iconKey: 'CheckSquare',
    groupId: 'workflows', order: 100, isPinnable: true,
    // Same roles as the /approvals RoleGuard in App.js; the route has no module gate.
    roles: ['system_admin', 'meridian_approver', 'partner_admin', 'partner_viewer'] },

  // -------------------------- OPERATIONS --------------------------------
  { name: 'Work Queue', href: '/work-queue', testId: 'nav-work-queue',
    iconKey: 'Inbox', groupId: 'operations', order: 10, isPinnable: true,
    roles: ['meridian_operator', 'system_admin',
            'partner_admin', 'partner_viewer'], highlight: true },
  { name: 'Approvals Queue', href: '/approver-queue',
    testId: 'nav-approvals-queue', iconKey: 'CheckSquare',
    groupId: 'operations', order: 20, isPinnable: true,
    roles: ['meridian_approver'], highlight: true },
  { name: 'Review Queue', href: '/review-queue', testId: 'nav-review-queue',
    iconKey: 'ScanSearch', groupId: 'operations', order: 30,
    isPinnable: true,
    roles: ['system_admin', 'client_admin', 'merchant_admin',
            'merchant_operator', 'meridian_operator', 'meridian_approver',
            'partner_admin', 'partner_viewer'] },
  { name: 'All Cases', href: '/cases', testId: 'nav-all-cases',
    iconKey: 'FileText', groupId: 'operations', order: 40,
    isPinnable: true, module: NAV_MODULES.UK_WORKFLOWS,
    roles: ['system_admin', 'client_admin', 'viewer', 'meridian_operator', 'meridian_approver',
            'partner_admin', 'partner_viewer'] },
  { name: 'HMRC Notifications', href: '/admin/hmrc-notifications',
    testId: 'nav-hmrc-notifications', iconKey: 'Bell',
    groupId: 'operations', order: 60, isPinnable: true,
    module: NAV_MODULES.UK_WORKFLOWS,
    roles: [..._INTERNAL_SYSADMIN, 'partner_admin', 'partner_viewer'] },
  // Iter S25 Phase 30 (Fix 30.3) — cross-workflow filing outcomes.
  { name: 'Filing Outcomes', href: '/filing-outcomes',
    testId: 'nav-filing-outcomes', iconKey: 'BarChart3',
    groupId: 'operations', order: 65, isPinnable: true,
    roles: ['system_admin', 'meridian_operator'] },
  // Iter236 Phase 2 — Recent Activity is now a first-class Operations
  // nav item (was a fixed sidebar footer toggle). `type: 'sheet_toggle'`
  // tells Sidebar.jsx to render it as a button that opens the right-
  // hand ActivityFeed drawer instead of a NavLink.
  { name: 'Recent Activity', href: null, testId: 'nav-recent-activity',
    iconKey: 'Activity', groupId: 'operations', order: 70,
    isPinnable: false, type: 'sheet_toggle', sheetId: 'activity-feed',
    roles: _ALL },

  // ------------------------ INSIGHTS & REPORTS --------------------------
  { name: 'Insights', href: '/insights', testId: 'nav-insights',
    iconKey: 'TrendingUp', groupId: 'insights', order: 10,
    isPinnable: true,
    roles: ['system_admin', 'client_admin', 'merchant_admin',
            'merchant_operator', 'merchant_viewer', 'viewer',
            'meridian_operator', 'meridian_approver',
            'broker_collaborator',
            'partner_admin', 'partner_viewer'] },
  { name: 'Reports', href: '/reports', testId: 'nav-reports',
    iconKey: 'BarChart3', groupId: 'insights', order: 20,
    isPinnable: true,
    roles: ['system_admin', 'client_admin', 'viewer', 'meridian_approver',
            'partner_admin', 'partner_viewer'] },

  // ----------------------- DATA & INTEGRATIONS --------------------------
  { name: 'Connectors', href: '/connectors', testId: 'nav-connectors',
    iconKey: 'Plug', groupId: 'data', order: 30, isPinnable: true,
    module: NAV_MODULES.UK_WORKFLOWS,
    roles: _INTERNAL_OPS },
  { name: 'Integrations', href: '/settings/integrations',
    testId: 'nav-integrations', iconKey: 'Plug',
    groupId: 'data', order: 40, isPinnable: true,
    roles: [..._MERCHANT_ADMIN, 'partner_admin'] },
  { name: 'Sync Data', href: '/sync-data', testId: 'nav-sync-data',
    iconKey: 'Database', groupId: 'data', order: 50, isPinnable: true,
    module: NAV_MODULES.UK_WORKFLOWS,
    roles: _INTERNAL_OPS },
  { name: 'Sample Data', href: '/sample-data', testId: 'nav-sample-data',
    iconKey: 'Database', groupId: 'data', order: 60, isPinnable: true,
    module: NAV_MODULES.UK_WORKFLOWS,
    roles: _INTERNAL_OPS },
  { name: 'HMRC OAuth', href: '/hmrc-oauth', testId: 'nav-hmrc-oauth',
    iconKey: 'ShieldCheck', groupId: 'data', order: 80, isPinnable: true,
    module: NAV_MODULES.UK_WORKFLOWS,
    roles: ['system_admin', 'meridian_operator', 'client_admin',
            'partner_admin'] },

  // ------------------------------- ADMIN --------------------------------
  { name: 'Workspace Settings', href: '/admin/settings',
    testId: 'nav-workspace-settings', iconKey: 'Settings2',
    groupId: 'admin', order: 10, isPinnable: true,
    // Iter263 Phase U5 Bug 3 — per-workspace endpoint; hide in Mode A.
    hideInCrossMode: true,
    roles: ['system_admin', 'client_admin', 'partner_admin'] },
  { name: 'Settings', href: '/settings', testId: 'nav-settings',
    iconKey: 'Settings', groupId: 'admin', order: 20, isPinnable: true,
    roles: ['client_admin'] },
  { name: 'Billing', href: '/settings/billing', testId: 'nav-billing',
    iconKey: 'CreditCard', groupId: 'admin', order: 30, isPinnable: true,
    roles: ['system_admin', 'client_admin', 'merchant_admin', 'merchant_operator', 'merchant_viewer', 'viewer'] },
  { name: 'Billing Requests', href: '/settings/billing/requests',
    testId: 'nav-billing-requests', iconKey: 'Inbox',
    groupId: 'admin', order: 40, isPinnable: true,
    roles: ['system_admin'] },
  { name: 'System Admin', href: '/admin', testId: 'nav-system-admin',
    iconKey: 'Shield', groupId: 'admin', order: 50, isPinnable: true,
    roles: _INTERNAL_SYSADMIN },
  { name: 'CDS Platform', href: '/admin/cds-platform',
    testId: 'nav-cds-platform', iconKey: 'Activity',
    groupId: 'admin', order: 60, isPinnable: true,
    module: NAV_MODULES.UK_WORKFLOWS,
    roles: _INTERNAL_SYSADMIN },
  // Phase 31 — Rule Configuration (/import-review/rules) retired.
  { name: 'Audit Log', href: '/audit-logs', testId: 'nav-audit-log',
    iconKey: 'FileBarChart', groupId: 'admin', order: 80,
    isPinnable: true,
    roles: ['system_admin', 'client_admin', 'merchant_admin', 'merchant_operator', 'merchant_viewer', 'viewer', 'meridian_operator', 'meridian_approver', 'broker_collaborator', 'partner_admin', 'partner_viewer'] },

  // ------------------------------ GROWTH --------------------------------
  { name: 'Prospects', href: '/admin/prospects', testId: 'nav-prospects',
    iconKey: 'Users', groupId: 'growth', order: 10, isPinnable: true,
    roles: _INTERNAL_SYSADMIN },
  // ------------------------------ PARTNER (Iter263) ---------------------
  // Iter263 Phase M1 — Partner Console nav for partner_admin +
  // partner_viewer. Sysadmin already sees the Meridian operator
  // sidebar; the partner surface is a separate, focused set for the
  // 3PL/agency tier.
  { name: 'Overview', href: '/partner',
    testId: 'nav-partner-overview',
    iconKey: 'LayoutDashboard', groupId: 'partner', order: 10,
    isPinnable: true, roles: ['partner_admin', 'partner_viewer'] },
  { name: 'Merchants', href: '/partner?tab=merchants',
    testId: 'nav-partner-merchants',
    iconKey: 'Building2', groupId: 'partner', order: 20,
    isPinnable: true, roles: ['partner_admin', 'partner_viewer'] },
  { name: 'Returns', href: '/partner/returns',
    testId: 'nav-partner-returns',
    iconKey: 'Package', groupId: 'partner', order: 30,
    isPinnable: true, roles: ['partner_admin', 'partner_viewer'] },
  { name: 'Claims', href: '/partner?tab=claims',
    testId: 'nav-partner-claims',
    iconKey: 'FileText', groupId: 'partner', order: 40,
    isPinnable: true, roles: ['partner_admin', 'partner_viewer'] },
  // Iter263 Phase S3 — Filing Access surface visible only in Mode A
  // (cross-workspace). In Mode B the partner is inside a specific
  // merchant workspace and can trigger the locked tile directly.
  { name: 'Filing Access', href: '/partner/filing-access',
    testId: 'nav-partner-filing-access',
    iconKey: 'ShieldCheck', groupId: 'partner', order: 45,
    isPinnable: true, roles: ['partner_admin'],
    hideWhen: 'partnerWorkspaceMode' },
  { name: 'Insights & Reports', href: '/insights',
    testId: 'nav-partner-insights',
    iconKey: 'TrendingUp', groupId: 'partner', order: 50,
    isPinnable: true, roles: ['partner_admin', 'partner_viewer'] },
  { name: 'Sites / Warehouses', href: '/partner?tab=sites',
    testId: 'nav-partner-sites',
    iconKey: 'Truck', groupId: 'partner', order: 60,
    isPinnable: true, roles: ['partner_admin', 'partner_viewer'] },
  { name: 'Settings', href: '/partner?tab=settings',
    testId: 'nav-partner-settings',
    iconKey: 'Settings', groupId: 'partner', order: 70,
    isPinnable: true, roles: ['partner_admin', 'partner_viewer'] },
  // Access requests, sandbox invites and trial requests now live as tabs on /admin/prospects.
  { name: 'Upsell Signal', href: '/admin/analytics',
    testId: 'nav-admin-analytics', iconKey: 'TrendingUp',
    groupId: 'growth', order: 50, isPinnable: true,
    roles: _INTERNAL_SYSADMIN },
  // Iter263 Phase N3 — global feature flag admin (US_LANE_LIVE etc.)
  { name: 'Feature Flags', href: '/admin/feature-flags',
    testId: 'nav-feature-flags', iconKey: 'ShieldAlert',
    groupId: 'growth', order: 60, isPinnable: true,
    roles: _INTERNAL_SYSADMIN },

  // ------------------ HIDDEN / LEGACY (direct-URL only) ----------------
  // Kept reachable so bookmarks don't 404 — hidden from sidebar rendering.
  // Iter236 — Dashboard removed; App.jsx redirects /dashboard → /home.
  // Iter256 — Import Repayment v2 is no longer hidden; it lives in the
  // WORKFLOWS group above (see `nav-import-review`).
  { name: 'RGR Relief', href: '/rgr-returns',
    testId: 'nav-rgr-realtime', iconKey: 'RotateCcw',
    groupId: 'workflows', order: 999, isPinnable: false,
    module: NAV_MODULES.UK_WORKFLOWS,
    roles: ['system_admin', 'client_admin', 'meridian_operator', 'meridian_approver', '3pl_operator'],
    hideFromNav: true },
];


// Iter236 export — flat list of the 7 canonical groups in display order.
export const NAV_GROUP_ORDER = Object.values(NAV_GROUPS)
  .sort((a, b) => a.order - b.order);


/**
 * Return true if a given module is enabled for the active workspace.
 *
 * Iter S25 Phase 28 (Fix 2) — the sole source of truth is the
 * backend ``lanes_resolved`` block (``services/lanes.py::resolve_lanes``).
 * The frontend never re-derives lane state independently after this
 * change. Legacy read paths (``feature_flags``, ``enabled_lanes``,
 * partner-level ``lanes_enabled``) are retained ONLY as a transient
 * fallback for cached workspace records rendered before the backend
 * started emitting ``lanes_resolved``. No new call site should rely
 * on that fallback.
 */
export function isModuleEnabled(featureFlags, module, partnerEntitlements,
                                 enabledLanes, lanesResolved) {
  if (!module) return true;
  // Phase 28 canonical path — trust the backend resolver.
  if (lanesResolved && typeof lanesResolved === 'object') {
    if (module === NAV_MODULES.EU_LANE) return lanesResolved.eu === true;
    if (module === NAV_MODULES.USA_LANE) return lanesResolved.usa === true;
    if (module === NAV_MODULES.UK_WORKFLOWS) return lanesResolved.uk === true;
    return true;
  }
  // Legacy fallback — pre-Phase-28 caller / cached record.
  const flags = featureFlags || {};
  const hasWorkspaceFlags = featureFlags !== null
    && featureFlags !== undefined
    && Object.keys(flags).length > 0;
  const laneList = Array.isArray(enabledLanes) ? enabledLanes : null;
  const hasEnabledLanes = laneList !== null && laneList.length > 0;
  const laneHas = (token) => hasEnabledLanes
    && laneList.some((l) => String(l).toLowerCase() === token);
  if (module === NAV_MODULES.EU_LANE) {
    if (hasWorkspaceFlags) return flags.eu_lane === true;
    return partnerEntitlements?.lanes_enabled?.eu === true;
  }
  if (module === NAV_MODULES.USA_LANE) {
    if (hasWorkspaceFlags || hasEnabledLanes) return laneHas('us');
    const laneUs = partnerEntitlements?.lanes_enabled?.us;
    if (laneUs !== undefined) return laneUs === true;
    return partnerEntitlements?.lanes_enabled?.usa === true;
  }
  if (module === NAV_MODULES.UK_WORKFLOWS) {
    if (hasWorkspaceFlags) return flags.uk_workflows !== false;
    const partnerUk = partnerEntitlements?.lanes_enabled?.uk;
    return partnerUk === undefined ? true : partnerUk === true;
  }
  return true;
}


/**
 * Iter236 — build the sidebar-ready structure for a given role +
 * feature-flag set. Returns a FLAT array with `group_header` items
 * interleaved between visible group items so the existing Sidebar
 * render loop (which already knows about `type: 'group_header'`) keeps
 * working. Groups with no visible items are dropped automatically.
 *
 * `role` — canonical role string (aliases mapped: client_admin →
 *          merchant_admin, viewer → merchant_viewer, operator →
 *          merchant_operator when comparing role sets).
 * `featureFlags` — object; see `isModuleEnabled`.
 * `options.crossWorkspaceMode` — sysadmin viewing "all workspaces"
 *          bypasses the module filter so operators keep visibility.
 */
export function buildSidebarWithGroups(role, featureFlags, options = {}) {
  const {
    crossWorkspaceMode = false,
    partnerWorkspaceMode = false,
    partnerEntitlements = null,
    isPartnerCrossMode = false,
    enabledLanes = null,
  } = options;
  // Iter263 Phase R (sidebar mode switch) — when a partner has an
  // active merchant workspace open, hide the partner-console groups
  // (`partner`) and surface the standard workflow groups instead.
  // When partner is in "All workspaces" mode, keep the partner
  // console groups visible and hide the workspace-only surfaces.
  const isPartner = role === 'partner_admin' || role === 'partner_viewer';
  const hideGroups = new Set();
  if (isPartner) {
    if (partnerWorkspaceMode) {
      hideGroups.add('partner');
    }
    // Iter263 Phase U5 (Bug 4) — Mode A dedup. Partner cross-workspace
    // mode surfaces the /partner/* section only; the merchant sidebar
    // (Workflows / Operations / Insights / Data / Admin) is
    // duplicative in Mode A since Claims, Filing Access, Insights,
    // Sites, Warehouse, Settings all live under `partner`.
    if (isPartnerCrossMode && !partnerWorkspaceMode) {
      hideGroups.add('workflows');
      hideGroups.add('operations');
      hideGroups.add('insights');
      hideGroups.add('data');
      hideGroups.add('admin');
    }
  }
  // Filter leaves by role + module.
  const visibleLeaves = SIDEBAR_NAV.filter((item) => {
    if (item.hideFromNav) return false;
    if (item.roles && !item.roles.includes(role)) return false;
    if (hideGroups.has(item.groupId)) return false;
    // Iter263 Phase S3 — mode-specific hiding. `hideWhen` names a
    // localStorage-derived mode: `partnerWorkspaceMode` hides the
    // entry when the partner is scoped to a merchant workspace.
    if (item.hideWhen === 'partnerWorkspaceMode' && partnerWorkspaceMode) return false;
    // Iter263 Phase U5 (Bug 3) — some entries opt-out of Mode A via
    // `hideInCrossMode: true` (e.g. Workspace Settings needs an
    // active workspace and 500s if none is selected).
    if (item.hideInCrossMode && isPartnerCrossMode) return false;
    if (crossWorkspaceMode) return true;
    // Iter263 Phase U5 (Bug 2) — module-gated items are HIDDEN, not
    // locked, when the workspace's feature flag is off. Sidebar entries
    // gated by `module: 'uk_workflows'` are removed for EU-only tenants
    // and vice versa.
    return isModuleEnabled(featureFlags, item.module, partnerEntitlements, enabledLanes);
  }).map((item) => {
    // Iter263 Phase T4 — the Home / Overview entry points at
    // `/partner` (Partner Overview) in Mode A and at `/home`
    // (HomeLauncherPage) in Mode B. Highlight stays on in both.
    if (isPartnerCrossMode && item.testId === 'nav-home') {
      return { ...item, href: '/partner', name: 'Partner Home' };
    }
    return item;
  });
  // Bucket into groups + sort each bucket by `order`.
  const byGroup = {};
  visibleLeaves.forEach((item) => {
    const gid = item.groupId || 'home';
    (byGroup[gid] = byGroup[gid] || []).push(item);
  });
  const isInternal = /^(system_admin|meridian_)/.test(role || '');
  const output = [];
  NAV_GROUP_ORDER.forEach((group) => {
    if (group.visibility === 'internal_only' && !isInternal) return;
    const items = (byGroup[group.id] || []).slice().sort(
      (a, b) => (a.order || 0) - (b.order || 0),
    );
    if (items.length === 0) return;
    output.push({
      type: 'group_header',
      name: group.label,
      testId: `nav-group-${group.id}`,
      iconKey: group.iconKey,
      groupId: group.id,
    });
    items.forEach((it) => output.push(it));
  });
  return output;
}


/**
 * Legacy filter — kept so existing callers that pass the entire
 * SIDEBAR_NAV in still work while migrations complete. New callers
 * should prefer `buildSidebarWithGroups()` which handles the group
 * header injection.
 */
export function filterSidebarNav(items, role, featureFlags, options = {}) {
  const { crossWorkspaceMode = false, partnerEntitlements = null,
           enabledLanes = null } = options;
  const passes = (items || []).filter((item) => {
    if (item.hideFromNav) return false;
    if (item.roles && !item.roles.includes(role)) return false;
    if (crossWorkspaceMode) return true;
    return isModuleEnabled(featureFlags, item.module, partnerEntitlements, enabledLanes);
  });
  const result = [];
  for (let i = 0; i < passes.length; i += 1) {
    const item = passes[i];
    if (item.type === 'group_header') {
      let hasChild = false;
      for (let j = i + 1; j < passes.length; j += 1) {
        if (passes[j].type === 'group_header') break;
        hasChild = true;
        break;
      }
      if (!hasChild) continue;
    }
    result.push(item);
  }
  return result;
}


// -------------------------------------------------------------------
// Iter256 — Subtree grouping (pure, testable)
// -------------------------------------------------------------------

/**
 * Fold items with `parentHref` into their parent's `children[]`.
 *
 *   [{href:'/a'}, {href:'/a/1', parentHref:'/a'}, {href:'/b'}]
 * → [{href:'/a', children:[{href:'/a/1', parentHref:'/a'}]},
 *    {href:'/b', children:[]}]
 *
 * Orphan children (parent hidden by role gating) are flattened back to
 * the top level so they remain reachable.
 */
export function buildSubtreeGroups(items) {
  const parents = [];
  const parentIndex = new Map();
  (items || []).forEach((it) => {
    if (it && !it.parentHref) {
      parentIndex.set(it.href, parents.length);
      parents.push({ ...it, children: [] });
    }
  });
  (items || []).forEach((it) => {
    if (!it || !it.parentHref) return;
    const idx = parentIndex.get(it.parentHref);
    if (idx == null) {
      parents.push({ ...it, children: [] });
    } else {
      parents[idx].children.push(it);
    }
  });
  return parents;
}

/**
 * True when the subtree rooted at `parent` should auto-expand based on
 * the current pathname — i.e. the active route matches the parent or
 * any of its children.
 */
export function shouldAutoExpandSubtree(parent, currentPath) {
  if (!parent || !currentPath) return false;
  const hrefs = [parent.href, ...(parent.children || []).map((c) => c.href)]
    .filter(Boolean);
  return hrefs.some((h) => currentPath === h || currentPath.startsWith(h + '/'));
}
